import {inject} from '@angular/core';
import {PartialMatchRouteSnapshot, Router, Routes, UrlTree} from '@angular/router';

/**
 * ADMIN › Client accounts is gone: a client's logins are per-client work, so they live on the
 * client's own record, the Accounts Management tab (docs/ui/client-360.md §7 row 6,
 * docs/ui/admin.md §2 row 1). A saved link that names a client opens that client there; one that
 * names none opens Clients, where the account manager picks the client. It builds a UrlTree
 * because a plain string redirectTo cannot carry `?tab=`.
 */
export const clientAccountsRedirect = (
  {queryParamMap}: Pick<PartialMatchRouteSnapshot, 'queryParamMap'>
): UrlTree => {
  const router = inject(Router);
  const businessId = queryParamMap.get('businessId');
  return businessId
    ? router.createUrlTree(['/businesses', businessId], {queryParams: {tab: 'credentials'}})
    : router.createUrlTree(['/businesses']);
};

export const routes: Routes = [
  // the shared front door, built in @shared so juno and argus cannot drift apart again; what differs
  // between them is APP_INFO in app.config (docs/ui/sign-in.md). The path does not move, so
  // AuthConfig.loginPath, the shell's chromeless-route rule and every ?reason=expired redirect stand.
  {path: 'login', loadComponent: () => import('@libs/ui/sign-in/sign-in.component').then(m => m.SignInComponent)},
  {path: 'version', loadComponent: () => import('@libs/ui/version/version.component').then(m => m.VersionComponent)},
  // an employee's own login and password; any signed-in employee, no ops gate (docs/ui/my-account.md)
  {path: 'my-account', loadComponent: () => import('@libs/ui/my-account/my-account.component').then(m => m.MyAccountComponent)},
  // Admin group (plan §3 phase A). open to every employee; rank rules are the server's.
  {
    path: 'admin/data',
    loadComponent: () => import('./components/admin/data/data.component').then(m => m.DataComponent),
  },
  {
    path: 'admin/employees',
    loadComponent: () => import('./components/admin/employees/employees.component')
      .then(m => m.EmployeesComponent)
  },
  // the screen moved onto the client record on 2026-09-04; the old link still lands (above the catch-all)
  {path: 'admin/client-accounts', redirectTo: clientAccountsRedirect},
  // every employee reaches every screen (VT, 2026-09-04); a server gate a role does not pass answers on screen, not here
  {path: 'onboarding', loadComponent: () => import('./components/onboarding/onboarding.component').then(m => m.OnboardingComponent)},
  // SOURCE > Salons: the market record, one item per vertical (docs/ui/salons-ui.md §2 row 11)
  {path: 'salons', loadComponent: () => import('./components/salons/salons.component').then(m => m.SalonsComponent)},
  // the screen was /raw-place-data until 2026-09-07; a bookmark keeps working and the slice rides along
  {path: 'raw-place-data', redirectTo: 'salons'},
  {
    path: 'businesses',
    children: [
      {path: '', loadComponent: () => import('./components/businesses/businesses.component').then(m => m.BusinessesComponent)},
      {path: ':businessId/create-store', loadComponent: () => import('./components/create-store/create-store.component').then(m => m.CreateStoreComponent), data: {action: 'create'}},
      {path: ':businessId/store-detail/:storeId/contracts/create', loadComponent: () => import('./components/store-detail/store-contracts/create-contract/create-contract.component').then(m => m.CreateContractComponent)},
      {path: ':businessId/store-detail/:storeId/contracts/:contractId', loadComponent: () => import('./components/store-detail/store-contracts/contract-detail/contract-detail.component').then(m => m.ContractDetailComponent)},
      {path: ':businessId/store-detail/:storeId', loadComponent: () => import('./components/store-detail/store-detail.component').then(m => m.StoreDetailComponent), data: {action: 'update'}},
      {path: ':businessId', loadComponent: () => import('./components/business-detail/business-detail.component').then(m => m.BusinessDetailComponent),},
      {path: ':businessId/update', loadComponent: () => import('./components/update-business/update-business.component').then(m => m.UpdateBusinessComponent)},
    ]
  },
  // Friday Tech's own partners: decided, not built. The page says so (docs/ui/affiliates.md).
  {path: 'affiliates', pathMatch: 'full', loadComponent: () => import('./components/affiliates/affiliates.component').then(m => m.AffiliatesComponent)},
  // hermes' screens moved from /affiliates to /influencers on 2026-09-03; saved links still land
  {path: 'affiliates/create', redirectTo: 'influencers/create'},
  {path: 'affiliates/:influencerId/update', redirectTo: 'influencers/:influencerId/update'},
  {path: 'affiliates/:influencerId', redirectTo: 'influencers/:influencerId'},
  {
    path: 'influencers',
    children: [
      {path: '', loadComponent: () => import('./components/influencer-list/influencer-list.component').then(m => m.InfluencerListComponent)},
      {path: 'create', loadComponent: () => import('./components/influencer-list/create-influencer/create-influencer.component').then(m => m.CreateInfluencerComponent)},
      {path: ':influencerId/update', loadComponent: () => import('./components/influencer-detail/update-influencer/update-influencer.component').then(m => m.UpdateInfluencerComponent)},
      {path: ':influencerId', loadComponent: () => import('./components/influencer-detail/influencer-detail.component').then(m => m.InfluencerDetailComponent)},
    ]
  },
  // the Accounts pages merged into the business detail page; redirects keep
  // old bookmarks and deep links alive (query params carry over)
  {path: 'accounts/:accountId', redirectTo: 'businesses/:accountId'},
  {path: 'accounts', redirectTo: 'businesses'},
  {
    path: 'products',
    loadComponent: () => import('./components/product-management/product-management.component').then(m => m.ProductManagementComponent),
  },
  {
    path: 'invoices',
    loadComponent: () => import('./components/invoices/invoices.component').then(m => m.InvoicesComponent),
  },
  {
    path: 'payouts',
    loadComponent: () => import('./components/payouts/payouts.component').then(m => m.PayoutsComponent),
  },
  {
    path: 'subscription-management',
    loadComponent: () => import('./components/subscription-management/subscription-management.component').then(m => m.SubscriptionManagementComponent),
  },
  {
    path: 'subscription-management/:businessId/items',
    loadComponent: () => import('./components/subscription-management/manage-items/manage-items.component')
      .then(m => m.ManageItemsComponent)
  },
  {
    path: 'leads',
    loadComponent: () => import('./components/client-contacts/client-contacts.component').then(m => m.ClientContactsComponent),
  },
  // the screen was /client-contacts until 2026-09-03; a bookmark or a shared link keeps working, and the query string rides along
  {path: 'client-contacts', redirectTo: 'leads'},
  // every role lands on Businesses (plan Q28)
  {path: '', redirectTo: 'businesses', pathMatch: 'full'},
  {path: '**', redirectTo: 'businesses'}
];
